import React, { useState, useEffect } from 'react';
import { X, RotateCcw, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function VersionHistoryModal({ prompt, onClose, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/prompts/${prompt.prompt_id}/versions`, {
        withCredentials: true,
        headers
      });
      setVersions(data);
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (versionId) => {
    if (!window.confirm('Restore this version? Current version will be saved to history.')) return;

    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/api/prompts/${prompt.prompt_id}/restore/${versionId}`,
        {},
        { withCredentials: true, headers }
      );
      toast.success('Version restored successfully');
      onRestore();
    } catch (error) {
      toast.error('Failed to restore version');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="version-history-modal">
      <div className="bg-white rounded-xl p-6 w-[360px] max-h-[560px] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock size={20} className="text-[#2C4C3B]" />
            <h2 className="text-xl tracking-tight font-semibold text-[#1C1917]">Version History</h2>
          </div>
          <button onClick={onClose} className="text-[#78716C] hover:text-[#1C1917]" data-testid="close-modal-button">
            <X size={20} />
          </button>
        </div>

        <p className="text-sm text-[#57534E] mb-4">
          Viewing history for: <strong>{prompt.title}</strong>
        </p>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B] mx-auto"></div>
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-8">
            <Clock size={48} className="mx-auto text-[#E7E5E4] mb-3" />
            <p className="text-sm text-[#78716C]">No version history yet</p>
            <p className="text-xs text-[#78716C] mt-1">History is created when you edit a prompt</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-[#F5F2EA] rounded-lg p-3 border-2 border-[#2C4C3B]">
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#2C4C3B] text-white">
                  Current Version
                </span>
              </div>
              <h3 className="text-sm font-medium text-[#1C1917] mb-1">{prompt.title}</h3>
              <p className="text-xs text-[#57534E] line-clamp-2">{prompt.content}</p>
            </div>

            {versions.map((version) => (
              <div
                key={version.version_id}
                className="bg-white rounded-lg border border-[#E7E5E4] p-3"
                data-testid={`version-${version.version_number}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#F5F2EA] text-[#78716C]">
                      Version {version.version_number}
                    </span>
                    <p className="text-xs text-[#78716C] mt-1">
                      {formatDistanceToNow(new Date(version.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRestore(version.version_id)}
                    className="inline-flex items-center justify-center rounded-lg px-2 py-1 text-xs font-medium transition-colors bg-[#2C4C3B] text-white hover:bg-[#243D30]"
                    data-testid="restore-version-button"
                  >
                    <RotateCcw size={12} className="mr-1" /> Restore
                  </button>
                </div>
                <h3 className="text-sm font-medium text-[#1C1917] mb-1">{version.title}</h3>
                <p className="text-xs text-[#57534E] line-clamp-3">{version.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}